// OP1WebBgSpaceButton: dock button that picks the page shown in the
// BACKGROUND, under the desktop (windows + dock). Look and the `isSelected`
// variant are designed in Plasmic; behaviour added here.
//
// UX rules (build review NOV.01.2026):
//   - Click -> variant becomes "isSelected" and the page shows in the
//     background. A second click does nothing.
//   - Only one button is selected at a time: a button is selected exactly
//     when its page is the one in the background (current URL), so two can
//     never be selected together.
//
// Prop `pagePath` (Text, set in Studio): the background page's URL path,
// e.g. "/community" (OP1Web/homepage) or "/community/forum" (OP1Web/forum).
// Switching pages is a client-side route change, so open windows stay
// exactly where they are (state lives in DesktopStateProvider).
// Without `pagePath` the button renders as designed (isSelected from Studio).
//
// This file is owned by us: `plasmic sync` never overwrites it.
import * as React from "react";
import { useInRouterContext, useLocation, useNavigate } from "react-router-dom";
import { PlasmicOp1WebBgSpaceButton } from "./plasmic/one_platform/PlasmicOp1WebBgSpaceButton";

const normalize = (p) => {
  if (!p) return "";
  const s = p.startsWith("/") ? p : `/${p}`;
  return s.length > 1 ? s.replace(/\/+$/, "").toLowerCase() : s;
};

// Router hooks are only legal inside a <Router>; isolate them so the button
// still renders (as designed) anywhere else.
function RoutedBgSpaceButton({ pagePath, onClick, forwardedRef, ...props }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = normalize(location.pathname) === normalize(pagePath);

  const handleClick = (...args) => {
    onClick?.(...args);
    if (!isSelected) navigate(pagePath); // second click: nothing
  };

  return (
    <PlasmicOp1WebBgSpaceButton
      {...props}
      root={{
        ref: forwardedRef,
        "aria-pressed": isSelected,
        "data-selected": isSelected ? "true" : "false",
      }}
      isSelected={isSelected}
      onClick={handleClick}
    />
  );
}

function Op1WebBgSpaceButton_({ pagePath, ...props }, ref) {
  const inRouter = useInRouterContext();
  if (pagePath && inRouter) {
    return <RoutedBgSpaceButton pagePath={pagePath} forwardedRef={ref} {...props} />;
  }
  return <PlasmicOp1WebBgSpaceButton root={{ ref }} {...props} />;
}

const Op1WebBgSpaceButton = React.forwardRef(Op1WebBgSpaceButton_);

export default Op1WebBgSpaceButton;
